// captureShare — カメラキャプチャ (PNG 保存) のファイル名と保存経路の方針。
//
// 背景 (docs/_wip/capture-download-blob-2026-08-13.md):
//   旧実装は `toDataURL` の戻りを `<a href>` に直接載せていたため、本番 fb で 2 件の
//   別々の失敗を出した:
//     ① Chromium は URL 長を 2MB (`kMaxURLChars`) に制限しており、描き込んで PNG が
//        1.5MB を超えると base64 化で 2MB を突破し、**エラーも出さず DL が失敗**する
//        (Edge/Win10 の「入室直後は動くが描いてから無反応」)。
//     ② iOS Safari の DL マネージャは `data:` URL を処理できない。staging 実機検証で
//        「真っ白でも失敗 / DL 確認ダイアログは出る / 確定後に無反応」を確認した
//        (= user gesture は生きており、`data:` の取得段で落ちている)。
//   ①② とも **Blob URL + `<a download>`** で消える。保存経路はこれ一本。
//
// **Web Share (files) は「保存」導線には使わない** (§4.2 改訂、2026-08-13 staging 実機):
//   当初は iOS で共有シート (→ 写真アプリ) を第一経路に置いた。実機で分かった切り分け:
//     - アプリ側の blob DL ボタン (タイムラプス「動画を保存」) → **動く**
//     - 共有シート → 他アプリへの共有 → **動く** (= Web Share の受け渡し自体は健全)
//     - 共有シート内の「画像/動画を保存」「ファイルに保存」→ **無反応**
//       (「ファイルに保存」はフォルダ選択まで出て保存されない = iOS 側の保存アクションが
//        死んでいる。端末固有の可能性が高く、こちらから直せない)
//   決定打は **失敗を検知できない**こと: `navigator.share()` は共有シートを出した時点で
//   resolve するので、その後ユーザーの「画像を保存」が不発でも例外は飛ばない。つまり
//   **DL へのフォールバックが原理的に書けず、silent failure が残る**。着地点が端末事情で
//   死にうる上に検知もできない経路を「保存」の第一手に置くのは筋が悪いので、同一端末で
//   実証済みの blob DL に一本化する。
//   (iPad では保存先が写真アプリでなく Safari のダウンロードになるが、「動くこと」を優先。)
//   ※ 共有シート経由で他アプリに渡す導線が欲しくなったら、それは「保存」ではなく
//     **共有機能として別に足す** (タイムラプス共有と同じ位置付け)。

import { formatCaptureTimestamp } from './uiFeedback.js';

/** DL ファイル名 (= 旧実装踏襲。タイムラプス `oekaki-log-*.mp4` と同じ流儀)。 */
export const captureFileName = (date: Date): string => `oekaki-${formatCaptureTimestamp(date)}.png`;

/**
 * composite / encode の待ち上限 (§5 T4)。
 *
 * 本番 fb「入室直後は動いたが、ある程度描いてからキャプチャすると**クリックしても反応
 * しない**」(Edge/Win10) の真因は**未確定**だが、コード上「押しても何も起きない」が起きうる
 * 経路が実在する: worker composite (`compositeAll.request`) は応答が来なければ promise が
 * 永久に未解決のままで、UI には何の変化も出ない。真因が何であれ、**無反応で終わる経路を
 * 残さない**ために timeout を入れる。
 *
 * 1500² の composite + encode は通常 1 秒未満なので、10 秒は「異常」と判断して十分な余裕。
 */
export const CAPTURE_EXPORT_TIMEOUT_MS = 10_000;

/** 保存に失敗したときチャット欄に出す通知 (= 黙って終わらせない)。 */
export const CAPTURE_FAILED_MESSAGE =
  'キャプチャの保存に失敗しました。時間をおいてもう一度お試しください';

/**
 * `promise` を `ms` で打ち切る。時間切れは reject。
 *
 * 打ち切っても元の promise 自体はキャンセルできない (worker への request を取り消す手段が
 * ないため) が、**呼び出し側は待つのをやめてフォールバックに進める**。timer は成否に
 * かかわらず必ず解除する。
 */
export const withTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('capture export timeout')), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};
